/**
 * Centralized, pre-sized icon components and the shared `IconSize` scale.
 * @registry Common icon set (edit/save/delete/etc.) with consistent sizing.
 *
 * Ported from `library-m/feedback/icons/CommonIcons`. Prop surface and
 * exports kept identical (same `IconSize` scale, same icon component names)
 * so page migration is mechanical. For icons not covered here, import
 * directly from `@tabler/icons-react` and pass `size={IconSize.md}` etc.
 *
 * Usage:
 * ```tsx
 * import {EditIcon, IconSize} from 'src/design/atoms/icons/CommonIcons';
 * <EditIcon size={IconSize.sm} />
 * ```
 */

import type {
  IconProps as TablerIconProps} from '@tabler/icons-react';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconArrowLeft,
  IconCalendar,
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconClock,
  IconCode,
  IconCopy,
  IconDeviceFloppy,
  IconDownload,
  IconHash,
  IconInfoCircle,
  IconMail,
  IconMailOff,
  IconMessage,
  IconPencil,
  IconPlayerPlay,
  IconPlus,
  IconRefresh,
  IconServer,
  IconSettings,
  IconTrash,
  IconUpload,
  IconUser,
  IconX,
} from '@tabler/icons-react';
import type {FC} from 'react';

// --- Size Constants ---
// Use these for consistent icon sizing across the application

export const IconSize = {
  /** 10px - For timeline bullets, very compact elements */
  xs: 10,
  /** 14px - For compact/inline use (ActionCard.Action, InfoCard icons, sidebar) */
  sm: 14,
  /** 16px - For buttons, standard UI elements */
  md: 16,
  /** 18px - For slightly larger button icons */
  lg: 18,
  /** 20px - For page headers, prominent elements */
  xl: 20,
  /** 24px - For large display icons */
  xxl: 24,
} as const;

export type IconSizeValue = (typeof IconSize)[keyof typeof IconSize];

// --- Pre-configured Icon Components ---
// Each component is individually tree-shakeable.
// Props are passed through to underlying Tabler icons (size, color, stroke, className, style, etc.)

export interface IconProps extends Omit<TablerIconProps, 'size'> {
  size?: IconSizeValue | number;
}

// Action icons
export const EditIcon: FC<IconProps> = ({size = IconSize.md, ...props}) => (
  <IconPencil size={size} {...props} />
);

export const SaveIcon: FC<IconProps> = ({size = IconSize.md, ...props}) => (
  <IconDeviceFloppy size={size} {...props} />
);

export const CopyIcon: FC<IconProps> = ({size = IconSize.md, ...props}) => (
  <IconCopy size={size} {...props} />
);

export const DeleteIcon: FC<IconProps> = ({size = IconSize.md, ...props}) => (
  <IconTrash size={size} {...props} />
);

export const AddIcon: FC<IconProps> = ({size = IconSize.md, ...props}) => (
  <IconPlus size={size} {...props} />
);

export const RefreshIcon: FC<IconProps> = ({size = IconSize.md, ...props}) => (
  <IconRefresh size={size} {...props} />
);

export const CheckIcon: FC<IconProps> = ({size = IconSize.md, ...props}) => (
  <IconCheck size={size} {...props} />
);

export const CloseIcon: FC<IconProps> = ({size = IconSize.md, ...props}) => (
  <IconX size={size} {...props} />
);

export const PlayIcon: FC<IconProps> = ({size = IconSize.md, ...props}) => (
  <IconPlayerPlay size={size} {...props} />
);

// Navigation icons
export const BackIcon: FC<IconProps> = ({size = IconSize.md, ...props}) => (
  <IconArrowLeft size={size} {...props} />
);

export const ChevronRightIcon: FC<IconProps> = ({
  size = IconSize.md,
  ...props
}) => <IconChevronRight size={size} {...props} />;

export const ChevronDownIcon: FC<IconProps> = ({
  size = IconSize.md,
  ...props
}) => <IconChevronDown size={size} {...props} />;

// Informational icons
export const InfoIcon: FC<IconProps> = ({size = IconSize.md, ...props}) => (
  <IconInfoCircle size={size} {...props} />
);

export const AlertIcon: FC<IconProps> = ({size = IconSize.md, ...props}) => (
  <IconAlertCircle size={size} {...props} />
);

export const WarningIcon: FC<IconProps> = ({size = IconSize.md, ...props}) => (
  <IconAlertTriangle size={size} {...props} />
);

export const MessageIcon: FC<IconProps> = ({size = IconSize.md, ...props}) => (
  <IconMessage size={size} {...props} />
);

export const MailIcon: FC<IconProps> = ({size = IconSize.md, ...props}) => (
  <IconMail size={size} {...props} />
);

export const MailOffIcon: FC<IconProps> = ({size = IconSize.md, ...props}) => (
  <IconMailOff size={size} {...props} />
);

// Entity/data icons
export const UserIcon: FC<IconProps> = ({size = IconSize.md, ...props}) => (
  <IconUser size={size} {...props} />
);

export const ServerIcon: FC<IconProps> = ({size = IconSize.md, ...props}) => (
  <IconServer size={size} {...props} />
);

export const HashIcon: FC<IconProps> = ({size = IconSize.md, ...props}) => (
  <IconHash size={size} {...props} />
);

export const CodeIcon: FC<IconProps> = ({size = IconSize.md, ...props}) => (
  <IconCode size={size} {...props} />
);

export const SettingsIcon: FC<IconProps> = ({size = IconSize.md, ...props}) => (
  <IconSettings size={size} {...props} />
);

// Time icons
export const CalendarIcon: FC<IconProps> = ({size = IconSize.md, ...props}) => (
  <IconCalendar size={size} {...props} />
);

export const ClockIcon: FC<IconProps> = ({size = IconSize.md, ...props}) => (
  <IconClock size={size} {...props} />
);

// Data transfer icons
export const ExportIcon: FC<IconProps> = ({size = IconSize.md, ...props}) => (
  <IconDownload size={size} {...props} />
);

export const ImportIcon: FC<IconProps> = ({size = IconSize.md, ...props}) => (
  <IconUpload size={size} {...props} />
);
